import { useEffect, useRef } from "react";

// Shared stack of open modals' close-handlers, most-recently-opened last.
// A single document keydown listener routes Escape to the topmost (last) entry,
// so stacked modals close one at a time from the top down — never all at once.
const stack: Array<() => void> = [];

function handleKeydown(e: KeyboardEvent) {
  if (e.key !== "Escape") return;
  const top = stack[stack.length - 1];
  if (!top) return;
  e.stopPropagation();
  top();
}

/**
 * Close the topmost open modal on Escape.
 *
 * Call this inside a component that is mounted only while its modal is open
 * (mount = open, unmount = close), so the stack ordering matches what's on
 * screen. The handler may be a no-op (e.g. while a save is in flight) — it
 * still occupies the top of the stack, so Escape won't fall through to a
 * modal beneath it.
 */
export function useEscapeToClose(onClose: () => void) {
  const ref = useRef(onClose);
  useEffect(() => { ref.current = onClose; });

  useEffect(() => {
    const entry = () => ref.current();
    stack.push(entry);
    if (stack.length === 1) document.addEventListener("keydown", handleKeydown);
    return () => {
      const i = stack.lastIndexOf(entry);
      if (i !== -1) stack.splice(i, 1);
      if (stack.length === 0) document.removeEventListener("keydown", handleKeydown);
    };
  }, []);
}
